import { useCallback, useState, useEffect } from 'react'
import { useLanguage } from './useLanguage'

export type MeasurementUnitSystem = 'metric' | 'imperial'

const LOCAL_STORAGE_KEY = 'measurementUnitSystem'

// Module-level subscribers to keep all hook instances in sync
type Subscriber = (unitSystem: MeasurementUnitSystem) => void
const subscribers = new Set<Subscriber>()

function notifySubscribers(unitSystem: MeasurementUnitSystem) {
  subscribers.forEach(subscriber => subscriber(unitSystem))
}

export function useMeasurementUnitSystem() {
  const lang = useLanguage()
  const defaultUnitSystem: MeasurementUnitSystem = lang === 'en-US' || lang === 'en' ? 'imperial' : 'metric'

  const [unitSystem, setUnitSystemState] = useState<MeasurementUnitSystem>(() => {
    const storedUnitSystem = localStorage.getItem(LOCAL_STORAGE_KEY) as MeasurementUnitSystem | null
    return storedUnitSystem ?? defaultUnitSystem
  })

  useEffect(() => {
    // Subscribe to changes from other components
    const subscriber = (newUnitSystem: MeasurementUnitSystem) => {
      setUnitSystemState(newUnitSystem)
    }
    subscribers.add(subscriber)

    return () => {
      subscribers.delete(subscriber)
    }
  }, [])

  const setUnitSystem = useCallback((newUnitSystem: MeasurementUnitSystem) => {
    localStorage.setItem(LOCAL_STORAGE_KEY, newUnitSystem)
    // Notify all subscribed components
    notifySubscribers(newUnitSystem)
  }, [])

  return { unitSystem, setUnitSystem }
}
